import { defineMessages } from "react-intl";
import { all, call, put, select, takeEvery, takeLatest } from "redux-saga/effects";

import Organization from "@mapmycustomers/shared/types/Organization";
import ListResponse from "@mapmycustomers/shared/types/viewModel/ListResponse";
import ApiError from "@mapmycustomers/shared/util/api/ApiError";
import { showModal } from "@mapmycustomers/ui";

import i18nService from "@app/config/I18nService";
import CoachingNoteCategory from "@app/scene/dashboard/types/CoachingNoteCategory";
import { callApi, callReadonlyApi } from "@app/store/api/callApi";
import { handleError } from "@app/store/errors/actions";
import { getOrganizationId } from "@app/store/iam";
import getValidationErrors from "@app/util/errorHandling/getValidationErrors";

import {
  createCoachingNoteCategory,
  deleteCoachingNoteCategory,
  fetchCoachingNoteCategories,
  reorderCoachingNoteCategories,
  updateCoachingNoteCategory,
} from "./actions";
import { getCoachingNoteCategories } from "./selectors";

const messages = defineMessages({
  deleteHasNotes: {
    id: "dashboard.coaching.categories.deleteHasNotes",
    defaultMessage:
      "This category can not be deleted because it's currently assigned to one or more notes. Please reassign those notes to another category, then try deleting again.",
    description:
      "Body of the modal shown when deleting a coaching note category that's still assigned to one or more notes.",
  },
  deleteHasNotesTitle: {
    id: "dashboard.coaching.categories.deleteHasNotes.title",
    defaultMessage: "Category can't be deleted",
    description:
      "Title of the modal shown when a coaching note category can't be deleted because notes use it.",
  },
});

const withPrompts = (category: CoachingNoteCategory) => ({
  ...category,
  prompts: category.prompts ?? [],
});

function* onFetchCoachingNoteCategories() {
  try {
    const orgId: Organization["id"] = yield select(getOrganizationId);
    const response: ListResponse<CoachingNoteCategory> = yield callReadonlyApi(
      "listCoachingNoteCategories",
      orgId,
      { $order: "displayOrder" }
    );
    yield put(fetchCoachingNoteCategories.success(response.data.map(withPrompts)));
  } catch (error) {
    yield put(fetchCoachingNoteCategories.failure());
    yield put(handleError({ error }));
  }
}

function* onCreateCoachingNoteCategory({
  payload,
}: ReturnType<typeof createCoachingNoteCategory.request>) {
  try {
    const orgId: Organization["id"] = yield select(getOrganizationId);
    const category: CoachingNoteCategory = yield callApi("createCoachingNoteCategory", orgId, {
      name: payload.name,
    });
    yield put(createCoachingNoteCategory.success(withPrompts(category)));
  } catch (error) {
    yield put(createCoachingNoteCategory.failure());
    yield put(handleError({ error }));
  }
}

function* onUpdateCoachingNoteCategory({
  payload,
}: ReturnType<typeof updateCoachingNoteCategory.request>) {
  try {
    const orgId: Organization["id"] = yield select(getOrganizationId);
    const { callback, categoryId, ...changes } = payload;
    const category: CoachingNoteCategory = yield callApi(
      "updateCoachingNoteCategory",
      orgId,
      categoryId,
      changes
    );
    yield put(updateCoachingNoteCategory.success(withPrompts(category)));
    if (callback) {
      yield call(callback);
    }
  } catch (error) {
    yield put(updateCoachingNoteCategory.failure());
    yield put(handleError({ error }));
  }
}

function* onDeleteCoachingNoteCategory({
  payload,
}: ReturnType<typeof deleteCoachingNoteCategory.request>) {
  try {
    const orgId: Organization["id"] = yield select(getOrganizationId);
    yield callApi("deleteCoachingNoteCategory", orgId, payload.categoryId);
    yield put(deleteCoachingNoteCategory.success({ categoryId: payload.categoryId }));
  } catch (error) {
    yield put(deleteCoachingNoteCategory.failure({ categoryId: payload.categoryId }));
    // The category is still assigned to notes (the server returns a 409 with a
    // reference count). We can't reassign them for the user — other users' notes
    // aren't theirs to edit — so ask them to reassign first, then delete.
    if (extractReferenceCount(error) !== undefined) {
      yield call(showModal, {
        content: i18nService.formatMessage(messages.deleteHasNotes),
        title: i18nService.formatMessage(messages.deleteHasNotesTitle),
        type: "warning",
        // Manage Categories opens from an Antd Popover (z-index 1030); lift the
        // modal above it so it doesn't render behind the still-open popover.
        zIndex: 1100,
      });
      return;
    }
    yield put(handleError({ error }));
  }
}

export const extractReferenceCount = (error: unknown): number | undefined => {
  if (!(error instanceof ApiError) || error.status !== 409) {
    return undefined;
  }
  const v1059 = getValidationErrors(error).find(
    (validationError) => validationError.code === "v1059"
  );
  if (!v1059) {
    return undefined;
  }
  // Prefer structured data.count; fall back to args[0] (BE also stringifies it for templating).
  if (typeof v1059.data?.count === "number") {
    return v1059.data.count;
  }
  const argsCount = Number(v1059.args?.[0]);
  return Number.isFinite(argsCount) ? argsCount : undefined;
};

function* onReorderCoachingNoteCategories({
  payload,
}: ReturnType<typeof reorderCoachingNoteCategories.request>) {
  try {
    const orgId: Organization["id"] = yield select(getOrganizationId);
    const currentCategories: CoachingNoteCategory[] = yield select(getCoachingNoteCategories);

    // The request reducer already applied the new order optimistically, so the
    // store no longer holds the pre-drag positions to diff against — persist
    // every category's new displayOrder to the BE.
    const updates = payload.orderedIds.map((id, index) => ({ id, displayOrder: index + 1 }));

    yield all(
      updates.map((row) =>
        callApi("updateCoachingNoteCategory", orgId, row.id, { displayOrder: row.displayOrder })
      )
    );

    const byId = new Map(currentCategories.map((category) => [category.id, category]));
    const reordered = payload.orderedIds
      .map((id, index) => {
        const category = byId.get(id);
        return category ? { ...category, displayOrder: index + 1 } : undefined;
      })
      .filter((category): category is CoachingNoteCategory => category !== undefined);

    yield put(reorderCoachingNoteCategories.success(reordered));
  } catch (error) {
    yield put(reorderCoachingNoteCategories.failure());
    yield put(fetchCoachingNoteCategories.request());
    yield put(handleError({ error }));
  }
}

export function* coachingNoteCategoriesSagas() {
  yield takeLatest(fetchCoachingNoteCategories.request, onFetchCoachingNoteCategories);
  yield takeEvery(createCoachingNoteCategory.request, onCreateCoachingNoteCategory);
  yield takeEvery(updateCoachingNoteCategory.request, onUpdateCoachingNoteCategory);
  yield takeEvery(deleteCoachingNoteCategory.request, onDeleteCoachingNoteCategory);
  yield takeEvery(reorderCoachingNoteCategories.request, onReorderCoachingNoteCategories);
}
